import React, { useMemo, useState } from "react";
import { CheckCircle2, Users } from "lucide-react";
import { formatNum } from "@/lib/api";

const sum = (rows, k) => rows.reduce((s, r) => s + Number(r[k] || 0), 0);

export const perPerson = (rows) => {
  const m = {};
  const add = (id, name, v, kind) => {
    if (!id || !v) return;
    const g = (m[id] = m[id] || { id, name: name || "—", total: 0, prims: new Set(), arastirma: 0 });
    g.total += v;
    g.prims.add(kind);
    if (kind.startsWith("a:")) g.arastirma += v;
  };
  rows.forEach((r) => {
    (r.giden_kisiler || []).forEach((gid, i) => {
      const v = Number(r.kisi_basi_detay?.[gid] ?? r.kisi_basi_prim ?? 0);
      add(gid, r.giden_isimleri?.[i], v, `g:${r.id}`);
    });
    add(r.arastiran_kisi_id, r.arastiran_isim, Number(r.arastirma_primi || 0), `a:${r.id}`);
  });
  return Object.values(m).map((g) => {
    const ids = [...new Set([...g.prims].map((k) => k.slice(2)))];
    return { ...g, ids, count: ids.length, total: Math.round(g.total * 100) / 100 };
  }).sort((a, b) => b.total - a.total);
};

export function MonthSummary({ rows, label, canSeePrim, canApprove, onApprove }) {
  const [open, setOpen] = useState(false);
  const pending = rows.filter((r) => r.onaylandi !== true);
  const people = useMemo(() => perPerson(pending), [pending]);
  const tah = sum(rows, "tahsilat_miktari");
  const vek = sum(rows, "vekalet_ucreti");
  const prim = sum(rows, "odenecek_toplam");
  const pPrim = sum(pending, "odenecek_toplam");
  const pTah = sum(pending, "tahsilat_miktari");

  const approve = () => {
    const lines = canSeePrim ? people.slice(0, 15).map((p) => `• ${p.name}: ${formatNum(p.total)} ₺ (${p.count} prim)`).join("\n") : "";
    const msg = `${label} · ${pending.length} bekleyen prim onaylanacak.\n\nTahsilat: ${formatNum(pTah)} ₺`
      + (canSeePrim ? `\nÖdenecek prim: ${formatNum(pPrim)} ₺\n\nPersonel bazında:\n${lines}${people.length > 15 ? `\n… +${people.length - 15} kişi` : ""}` : "")
      + "\n\nSon kontrol: tutarlar doğru mu? Onaylansın mı?";
    onApprove(pending.map((r) => r.id), msg);
  };

  const approvePerson = (p) => {
    const set = new Set(p.ids);
    const shared = pending.filter((r) => set.has(r.id) && ((r.giden_kisiler || []).length > 1 || (r.arastiran_kisi_id && r.arastiran_kisi_id !== p.id)));
    const msg = `${label} · ${p.name} için ${p.count} bekleyen prim onaylanacak.\nKişinin toplamı: ${formatNum(p.total)} ₺`
      + (shared.length ? `\n\nDikkat: ${shared.length} prim başka personelle ortak; onaylanınca o kişilerin payları da onaylanmış olur.` : "")
      + "\n\nOnaylansın mı?";
    onApprove(p.ids, msg);
  };

  return (
    <span className="relative inline-flex items-center gap-1.5 flex-wrap" data-testid="pending-month-summary">
      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 font-mono-tab">
        Tahsilat {formatNum(tah)} · Vekalet {formatNum(vek)}
        {canSeePrim && <> · <b data-testid="pending-month-prim-total">Prim {formatNum(prim)} ₺</b></>}
      </span>
      {canSeePrim && people.length > 0 && (
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid="pending-month-people-toggle"
          className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border transition-colors ${open ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50"}`}>
          <Users size={12} /> Personel ({people.length})
        </button>
      )}
      {canApprove && pending.length > 0 && (
        <button type="button" onClick={approve} data-testid="pending-month-approve"
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white transition-colors">
          <CheckCircle2 size={12} /> Bu Ayı Onayla ({pending.length})
        </button>
      )}
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 w-[480px] max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl p-2" data-testid="pending-month-people">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-1 pb-1">{label} · bekleyen prim (personel bazında)</div>
          <table className="w-full text-xs">
            <tbody>
              {people.map((p, i) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`pending-month-person-${i}`}>
                  <td className="py-1 px-1 font-semibold text-slate-800">{p.name}</td>
                  <td className="py-1 px-1 text-slate-500 text-right">{p.count} prim{p.arastirma ? ` · araştırma ${formatNum(p.arastirma)}` : ""}</td>
                  <td className="py-1 px-1 text-right font-mono-tab font-bold text-emerald-700">{formatNum(p.total)} ₺</td>
                  {canApprove && (
                    <td className="py-1 pl-2 text-right">
                      <button type="button" onClick={() => approvePerson(p)} data-testid={`pending-month-person-approve-${i}`}
                        title={`${p.name} primlerini onayla`}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors">
                        <CheckCircle2 size={11} /> Onayla
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300">
                <td className="py-1 px-1 font-bold">Toplam</td><td></td>
                <td className="py-1 px-1 text-right font-mono-tab font-bold" data-testid="pending-month-people-total">{formatNum(people.reduce((s, p) => s + p.total, 0))} ₺</td>
                {canApprove && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </span>
  );
}
